import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { publishedArticles, CATEGORIES, AUTHORS } from '@/content/articles';

const LOGO_URL       = "/logo-wordmark.png";
const CANONICAL_HOST = "https://meettokeep.com";
const CANONICAL_URL  = `${CANONICAL_HOST}/articles`;

function useHubHead(articleCount) {
  useEffect(() => {
    const prev = document.title;
    document.title = "Dating & Relationship Advice | Meet to Keep";
    const setMeta = (n, c, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${n}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, n); document.head.appendChild(el); }
      el.setAttribute('content', c);
    };
    const desc = "Practical guidance from Meet to Keep on online dating, serious relationships, compatibility, communication, dating safety, and intentional dating.";
    setMeta('description', desc);
    setMeta('og:title', 'Dating & Relationship Advice — Meet to Keep', 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:url', CANONICAL_URL, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:card', 'summary');
    // If the hub has zero published articles, don't index it yet.
    setMeta('robots', articleCount > 0 ? 'index, follow' : 'noindex, follow');
    let link = document.querySelector('link[rel="canonical"]');
    const hadLink = !!link;
    const prevHref = link?.getAttribute('href');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', CANONICAL_URL);
    return () => {
      document.title = prev;
      if (hadLink && prevHref) link.setAttribute('href', prevHref);
      else if (!hadLink && link) link.remove();
    };
  }, [articleCount]);
}

export default function Articles() {
  const navigate = useNavigate();
  const list = publishedArticles();
  useHubHead(list.length);

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]" data-testid="articles-hub">
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-white/90 backdrop-blur-xl border-b border-black/[0.04]">
        <button onClick={() => navigate('/')} aria-label="Meet to Keep home">
          <img src={LOGO_URL} alt="Meet to Keep" className="h-10 md:h-12 w-auto object-contain" />
        </button>
        <button onClick={() => navigate('/auth')}
          className="text-sm font-medium text-zinc-500 hover:text-[#1A1A1A] px-4 py-2 rounded-full border border-black/[0.08] hover:border-black/[0.15]">
          Sign in
        </button>
      </nav>

      <main className="pt-24">
        <section className="pt-8 pb-12 md:pt-16 md:pb-16 px-6">
          <div className="max-w-5xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4 text-xs text-zinc-500">
              <a href="/" className="hover:text-[#1A1A1A]">Home</a>
              <span className="mx-2">→</span>
              <span aria-current="page">Dating &amp; Relationship Advice</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-5" data-testid="articles-h1">
              Dating &amp; Relationship Advice
            </h1>
            <p className="text-base md:text-lg text-zinc-600 max-w-2xl leading-relaxed">
              Practical guidance from Meet to Keep on online dating, serious relationships, compatibility, communication, dating safety, and dating with intention.
            </p>
          </div>
        </section>

        <section className="pb-20 px-6">
          <div className="max-w-5xl mx-auto">
            {list.length === 0 ? (
              <div className="rounded-2xl border border-black/[0.06] p-10 text-center" data-testid="articles-empty">
                <p className="text-sm text-zinc-500 italic">New articles are being prepared. Check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
                {list.map((a) => {
                  const author = AUTHORS[a.author] || AUTHORS.editorial;
                  const cat = CATEGORIES[a.category]?.label || 'Article';
                  return (
                    <a key={a.slug} href={`/articles/${a.slug}`} className="group flex flex-col rounded-2xl border border-black/[0.06] overflow-hidden hover:border-black/[0.20] transition-colors bg-white" data-testid={`article-card-${a.slug}`}>
                      <div className="aspect-[16/10] bg-zinc-100">
                        {a.featuredImage?.src ? (
                          <img src={a.featuredImage.src} alt={a.featuredImage.alt || a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                        ) : null}
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-2">{cat}</p>
                        <h2 className="text-lg font-semibold text-[#1A1A1A] leading-snug mb-2">{a.title}</h2>
                        <p className="text-sm text-zinc-600 leading-relaxed mb-4 flex-1">{a.excerpt}</p>
                        <div className="flex items-center justify-between text-[11px] text-zinc-500">
                          <span>{author.name}</span>
                          <span>{a.readingTimeMin} min read</span>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
